import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Alert, AuthShell, Button, Field, Input } from '@aigovops/design-system';
import { isAuthDisabled, isWorkosMode, useAuth } from '../lib/auth';

/**
 * Sanitize a `?next=` query param to a same-origin path (prevents open
 * redirects). Only accepts strings that start with `/` and don't contain
 * a scheme or protocol-relative prefix.
 */
function sanitizeNext(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  return raw;
}

/**
 * Sign-in / sign-up page.
 *
 * PR-P0-4 · ADR-0015: `/signin` and `/signup` are the same component
 * with different default `mode`. WorkOS AuthKit surfaces both tabs
 * on its hosted UI; the mode just controls which tab is active on
 * landing (via `screen_hint=sign-up` on the /authorize URL).
 *
 * Three build modes, selected at build time (unchanged from pre-P0-4):
 *
 *   - WorkOS mode (VITE_WORKOS_CLIENT_ID set): single Continue button
 *     redirects to /api/auth/authorize, which bounces to WorkOS
 *     hosted AuthKit. On success Worker sets session cookie +
 *     redirects to / (AppRoot then routes to the default workspace).
 *
 *   - Mock mode (dev only, env unset, PROD=false): localStorage stub.
 *     Any email works. Sign-up + sign-in indistinguishable here.
 *
 *   - Disabled mode (production build without WorkOS): refuses to
 *     render any sign-in path.
 */
type SignInMode = 'signin' | 'signup';

const COPY: Record<
  SignInMode,
  {
    title: string;
    subtitle: string;
    ctaLabel: string;
    switchPrompt: string;
    switchLabel: string;
    switchHref: string;
  }
> = {
  signin: {
    title: 'Sign in',
    subtitle: 'Sign in to your GaCPro workspace.',
    ctaLabel: 'Continue',
    // Homepage refresh (#454): accounts start from an assessment request, same as the homepage CTA.
    // /signup stays reachable for invited users.
    switchPrompt: 'No account yet?',
    switchLabel: 'Request an assessment →',
    switchHref: '/#pilot',
  },
  signup: {
    title: 'Create your workspace',
    subtitle:
      'Get started in minutes. GaCPro creates your workspace as soon as you sign up.',
    ctaLabel: 'Continue',
    switchPrompt: 'Already have an account?',
    switchLabel: 'Sign in →',
    switchHref: '/signin',
  },
};

export function SignIn({ mode = 'signin' }: { mode?: SignInMode } = {}) {
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workos = isWorkosMode();
  const disabled = isAuthDisabled();
  const [email, setEmail] = useState('steveni@channelmatter.com');
  const [name, setName] = useState('S. Nielsen');
  const [workosHint, setWorkosHint] = useState('');

  const nextPath = sanitizeNext(searchParams.get('next'));
  const copy = COPY[mode];
  const isSignUp = mode === 'signup';

  function onWorkosStart(e?: FormEvent) {
    if (e) e.preventDefault();
    if (nextPath) {
      try {
        sessionStorage.setItem('gacpro.postLoginNext', nextPath);
      } catch {
        /* private-browsing lockdown — fall back to the default landing */
      }
    }
    if (isSignUp) {
      signUp(workosHint || undefined);
    } else {
      signIn(workosHint || undefined);
    }
  }

  function onMockSubmit(e: FormEvent) {
    e.preventDefault();
    signIn(email, name);
    if (nextPath) {
      navigate(nextPath);
      return;
    }
    if (user?.profile_id) navigate('/'); // AppRoot routes to the default workspace
    else navigate('/onboarding');
  }

  if (import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === 'true' && user) {
    return <Navigate to={nextPath ?? (user.default_workspace?.slug ? `/w/${user.default_workspace.slug}/app/dashboard` : '/workspaces')} replace />;
  }

  if (disabled) {
    return (
      <AuthShell
        title="Sign-in unavailable"
        subtitle="This deployment has not been configured with an identity provider."
      >
        <Alert variant="error">
          WorkOS is not configured for this build. No sign-in path is available
          until an operator provisions it.
        </Alert>
        <div
          style={{
            marginTop: 'var(--space-4)',
            display: 'grid',
            gap: 'var(--space-3)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-2xs)',
            color: 'var(--text-muted)',
          }}
        >
          <p style={{ margin: 0 }}>
            Operators: set{' '}
            <code style={{ fontFamily: 'var(--font-mono)' }}>VITE_WORKOS_CLIENT_ID</code> at
            build time and configure the WorkOS secrets on the Worker, then redeploy.
            The full checklist lives in{' '}
            <code style={{ fontFamily: 'var(--font-mono)' }}>docs/workos-setup.md</code>.
          </p>
          <p style={{ margin: 0 }}>
            Everyone else: sign-in will be available once the operator finishes
            provisioning.
          </p>
        </div>
      </AuthShell>
    );
  }

  const switchRow = (
    <p
      style={{
        marginTop: 'var(--space-4)',
        marginBottom: 0,
        fontSize: 'var(--fs-sm)',
        color: 'var(--text-muted)',
        textAlign: 'center',
      }}
    >
      {copy.switchPrompt}{' '}
      <Link
        to={copy.switchHref}
        style={{
          color: 'var(--accent)',
          fontWeight: 500,
          // Underlined: colour alone does not mark it as a link (WCAG 1.4.1).
          textDecoration: 'underline',
          textUnderlineOffset: 2,
        }}
      >
        {copy.switchLabel}
      </Link>
    </p>
  );

  if (workos) {
    return (
      <AuthShell title={copy.title} subtitle={copy.subtitle}>
        <Alert variant="info">
          {isSignUp
            ? "You'll be redirected to WorkOS to create your account. Personal-email sign-ups get a Personal Workspace automatically; corporate-email addresses discover their organization's workspace when it's already claimed."
            : "You'll be redirected to the hosted WorkOS login screen. If your organization uses SSO, we'll route you straight through it."}
        </Alert>
        <form
          onSubmit={onWorkosStart}
          style={{ display: 'grid', gap: 'var(--space-4)', marginTop: 'var(--space-4)' }}
        >
          <Field
            id="workos-hint"
            label="Work email"
            hint={
              isSignUp
                ? "We'll use this to check whether your organization already has a GaCPro workspace."
                : "Speeds sign-in — routes you straight to your company's identity provider."
            }
          >
            <Input
              type="email"
              value={workosHint}
              onChange={(e) => setWorkosHint(e.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
            />
          </Field>
          <Button
            type="submit"
            variant="primary"
            style={{ width: '100%', justifyContent: 'center' }}
          >
            {copy.ctaLabel}
          </Button>
        </form>
        {switchRow}
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={copy.title}
      subtitle="Mock auth — offline dev only. Production requires WorkOS."
    >
      <Alert variant="info">
        Any email works — this is the localStorage shim used during dev when
        WorkOS is not configured. Production builds refuse this path.
      </Alert>
      <form onSubmit={onMockSubmit} style={{ display: 'grid', gap: 'var(--space-4)', marginTop: 'var(--space-4)' }}>
        <Field id="email" label="Email">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field id="name" label="Display name">
          <Input type="text" required value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Button type="submit" variant="primary" style={{ width: '100%', justifyContent: 'center' }}>
          {copy.ctaLabel}
        </Button>
      </form>
      {switchRow}
    </AuthShell>
  );
}

/**
 * `/signup` route entry — same component with mode="signup" pre-selected.
 * Kept as a named export so App.tsx can render it directly without
 * re-implementing the SignIn state.
 */
export function SignUp() {
  return <SignIn mode="signup" />;
}
